import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import { Typography, Box, Container, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const StyledCard = styled(motion.div)(({ theme }) => ({
    cursor: 'pointer',
    perspective: '1500px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    transition: 'box-shadow 0.3s ease-in-out',
    '&:hover': {
        boxShadow: theme.shadows[6],
    },
    width: '100%',
    height: '400px',
    maxWidth: '800px',
    position: 'relative',
    transformStyle: 'preserve-3d',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden', // Prevent content from spilling out
}));

const CardFace = styled(motion.div)(({ theme }) => ({
    position: 'absolute',
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
    overflowY: 'auto',
    overflowX: 'hidden', // Prevent horizontal scrolling
}));

const CardBack = styled(CardFace)({
    transform: 'rotateY(180deg)',
});

const ContentWrapper = styled(Box)({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    overflowY: 'auto',
    overflowX: 'hidden', // Prevent horizontal scrolling
    padding: '20px',
    boxSizing: 'border-box', // Ensure padding is included in width calculation
});

const NavigationButton = styled(motion.div)(({ theme }) => ({
    cursor: 'pointer',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: theme.shadows[2],
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    '& svg': {
        fontSize: '2rem',
    },
}));

// Helper function to get full image URL
const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    return `${process.env.REACT_APP_API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

const NoteCardsPage = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { subscriptionId } = useParams();
    const location = useLocation();
    const testId = new URLSearchParams(location.search).get('test');
    const [questionIds, setQuestionIds] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isQuestionLoading, setIsQuestionLoading] = useState(true);
    const [direction, setDirection] = useState(0);

    const fetchQuestionIds = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/viewTests/${subscriptionId}?test=${testId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (response.data && response.data.status === 'success') {
                const fetchedQuestionIds = response.data.data.category.question_ids.map((q) => q._id);
                setQuestionIds(fetchedQuestionIds);
                setIsLoading(false);
            } else {
                throw new Error('Invalid test data received');
            }
        } catch (error) {
            console.error('Error fetching question IDs:', error);
            setIsLoading(false);
        }
    }, [subscriptionId, testId]);

    const fetchQuestion = useCallback(async (questionId) => {
        try {
            setIsQuestionLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setCurrentQuestion(response.data);
            setIsQuestionLoading(false);
        } catch (error) {
            console.error('Error fetching question:', error);
            setIsQuestionLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQuestionIds();
    }, [fetchQuestionIds]);

    useEffect(() => {
        if (questionIds.length > 0) {
            fetchQuestion(questionIds[currentIndex]);
        }
    }, [questionIds, currentIndex, fetchQuestion]);

    const handleFlip = () => {
        setIsFlipped(!isFlipped);
    };

    const handleNext = () => {
        setDirection(1);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % questionIds.length);
        setIsFlipped(false);
    };

    const handlePrevious = () => {
        setDirection(-1);
        setCurrentIndex((prevIndex) => (prevIndex - 1 + questionIds.length) % questionIds.length);
        setIsFlipped(false);
    };

    const cardVariants = {
        enter: (direction) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0,
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1,
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0,
        }),
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (questionIds.length === 0) {
        return (
            <Container maxWidth="sm" sx={{ mt: 4 }}>
                <Typography variant="h6" align="center">
                    No note cards available for this test.
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" align="center" gutterBottom>
                    Note Cards
                </Typography>
                <Typography variant="subtitle1" align="center" gutterBottom>
                    Card {currentIndex + 1} of {questionIds.length}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <NavigationButton onClick={handlePrevious} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} sx={{ mr: 2 }}>
                    <ChevronLeftIcon />
                </NavigationButton>
                <Box sx={{ flex: 1, maxWidth: '800px', display: 'flex', justifyContent: 'center' }}>
                    <AnimatePresence initial={false} custom={direction}>
                        {!isQuestionLoading && currentQuestion && (
                            <StyledCard
                                key={currentIndex}
                                custom={direction}
                                variants={cardVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{
                                    x: { type: 'spring', stiffness: 300, damping: 30 },
                                    opacity: { duration: 0.2 },
                                }}
                                onClick={handleFlip}
                                drag={isSmallScreen ? 'x' : false}
                                dragConstraints={{ left: 0, right: 0 }}
                                dragElastic={1}
                                onDragEnd={(e, { offset, velocity }) => {
                                    const swipe = swipePower(offset.x, velocity.x);
                                    if (swipe < -swipeConfidenceThreshold) {
                                        handleNext();
                                    } else if (swipe > swipeConfidenceThreshold) {
                                        handlePrevious();
                                    }
                                }}
                            >
                                <CardFace initial={false} animate={{ rotateY: isFlipped ? 180 : 0 }} transition={{ duration: 0.6 }}>
                                    <ContentWrapper>
                                        <Typography variant={isSmallScreen ? 'h6' : 'h5'} gutterBottom sx={{ fontWeight: 'bold' }}>
                                            Question:
                                        </Typography>
                                        <Typography
                                            variant={isSmallScreen ? 'body1' : 'h6'}
                                            sx={{
                                                mb: 2,
                                                overflowWrap: 'break-word',
                                                wordWrap: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {currentQuestion.question_text}
                                        </Typography>
                                        {currentQuestion.image && currentQuestion.image.url && (
                                            <Box sx={{ mt: 2, maxWidth: '100%', maxHeight: '200px' }}>
                                                <img
                                                    src={getImageUrl(currentQuestion.image.url)}
                                                    alt={currentQuestion.image.alt_text || 'Question image'}
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                />
                                            </Box>
                                        )}
                                    </ContentWrapper>
                                </CardFace>
                                <CardBack initial={false} animate={{ rotateY: isFlipped ? 0 : -180 }} transition={{ duration: 0.6 }}>
                                    <ContentWrapper>
                                        <Typography variant={isSmallScreen ? 'h6' : 'h5'} gutterBottom sx={{ fontWeight: 'bold' }}>
                                            Answer:
                                        </Typography>
                                        <Typography
                                            variant={isSmallScreen ? 'body1' : 'h6'}
                                            gutterBottom
                                            sx={{
                                                overflowWrap: 'break-word',
                                                wordWrap: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {currentQuestion.answers[`answer_${currentQuestion.correct_answer.toLowerCase()}`]}
                                        </Typography>
                                        {currentQuestion.reference && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: 2,
                                                    overflowWrap: 'break-word',
                                                    wordWrap: 'break-word',
                                                    whiteSpace: 'pre-wrap',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <strong>Reference:</strong> {currentQuestion.reference}
                                            </Typography>
                                        )}
                                        {currentQuestion.solution && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: 2,
                                                    overflowWrap: 'break-word',
                                                    wordWrap: 'break-word',
                                                    whiteSpace: 'pre-wrap',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <strong>Solution:</strong> {currentQuestion.solution}
                                            </Typography>
                                        )}
                                    </ContentWrapper>
                                </CardBack>
                            </StyledCard>
                        )}
                    </AnimatePresence>
                    {isQuestionLoading && (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
                <NavigationButton onClick={handleNext} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} sx={{ ml: 2 }}>
                    <ChevronRightIcon />
                </NavigationButton>
            </Box>
        </Container>
    );
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
};

export default NoteCardsPage;
